"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { hentSamtykke, SAMTYKKE_NOKKEL, SAMTYKKE_ENDRET, APNE_SAMTYKKE } from "@/lib/samtykke";

type ConsentState = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Ingen forhåndsavkryssede valg: samtykke skal være et aktivt ja (ekomloven).
  const [consent, setConsent] = useState<ConsentState>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Ikke vis cookie-banneret når siden er innebygd i en iframe (embed-widget på tredjeparts side)
    if (window.self !== window.top) return;
    // «Endre cookie-valg» (personvernsiden) åpner banneret igjen med dagens valg.
    const apne = () => {
      const s = hentSamtykke();
      setConsent({ necessary: true, analytics: !!s?.analytics, marketing: !!s?.marketing });
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener(APNE_SAMTYKKE, apne);
    if (!hentSamtykke()) {
      // Slight delay so it doesn't flash on first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => { clearTimeout(t); window.removeEventListener(APNE_SAMTYKKE, apne); };
    }
    return () => window.removeEventListener(APNE_SAMTYKKE, apne);
  }, []);

  function save(c: ConsentState) {
    try {
      localStorage.setItem(SAMTYKKE_NOKKEL, JSON.stringify({ ...c, timestamp: Date.now() }));
    } catch { /* privat modus: valget gjelder bare denne sidevisningen */ }
    const w = window as unknown as { gtag?: (...a: unknown[]) => void; fbq?: (...a: unknown[]) => void };
    // Er sporingen allerede lastet i denne økten (samtykke gitt tidligere), skal et
    // tilbaketrukket samtykke stoppe den med én gang — ikke først ved neste sidevisning.
    w.gtag?.('consent', 'update', {
      analytics_storage: c.analytics ? 'granted' : 'denied',
      ad_storage: c.marketing ? 'granted' : 'denied',
      ad_user_data: c.marketing ? 'granted' : 'denied',
      ad_personalization: c.marketing ? 'granted' : 'denied',
    });
    if (!c.marketing) w.fbq?.('consent', 'revoke');
    slettSporingscookies(c);
    window.dispatchEvent(new Event(SAMTYKKE_ENDRET));
    setVisible(false);
  }

  function acceptAll() {
    save({ necessary: true, analytics: true, marketing: true });
  }

  function acceptNecessary() {
    save({ necessary: true, analytics: false, marketing: false });
  }

  function saveCustom() {
    save(consent);
  }

  if (!visible) return null;

  return (
      <div
        role="dialog"
        aria-label="Informasjonskapsler"
        className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm"
      >
        <div className="bg-stone-900 border border-stone-700 rounded-2xl shadow-2xl shadow-stone-950/50 overflow-hidden">

          {/* Main content */}
          <div className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <svg viewBox="0 0 20 20" fill="none" className="w-5 h-5 flex-shrink-0">
                <circle cx="10" cy="10" r="9" fill="#a8a29e"/>
                <circle cx="7" cy="8" r="1.2" fill="#57534e"/>
                <circle cx="13" cy="7" r="1" fill="#57534e"/>
                <circle cx="10" cy="13" r="1.2" fill="#57534e"/>
                <circle cx="15" cy="12" r="0.8" fill="#57534e"/>
                <circle cx="6" cy="14" r="0.8" fill="#57534e"/>
              </svg>
              <div>
                <h2 className="text-white font-semibold text-sm leading-snug">
                  Vi bruker informasjonskapsler
                </h2>
                <p className="text-stone-400 text-xs mt-1 leading-relaxed">
                  Vi bruker cookies for å forbedre opplevelsen din og forstå hvordan tjenesten brukes.{" "}
                  <Link href="/personvern" className="text-stone-300 hover:text-white underline underline-offset-2 transition-colors">
                    Les mer
                  </Link>
                </p>
              </div>
            </div>

            {/* Expanded detail */}
            {expanded && (
              <div className="mt-4 mb-4 space-y-3 border-t border-stone-800 pt-4">
                {[
                  {
                    key: "necessary" as const,
                    label: "Nødvendige",
                    desc: "Kreves for at siden skal fungere. Kan ikke deaktiveres.",
                    locked: true,
                  },
                  {
                    key: "analytics" as const,
                    label: "Analyse",
                    desc: "Hjelper oss forstå hvilke sider som besøkes (Google Analytics).",
                    locked: false,
                  },
                  {
                    key: "marketing" as const,
                    label: "Markedsføring",
                    desc: "Brukes til å måle annonser og kampanjer på Facebook og Instagram (Meta).",
                    locked: false,
                  },
                ].map(item => (
                  <div key={item.key} className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-white text-xs font-medium">{item.label}</div>
                      <div className="text-stone-500 text-xs mt-0.5 leading-relaxed">{item.desc}</div>
                    </div>
                    <button
                      disabled={item.locked}
                      onClick={() => !item.locked && setConsent(p => ({ ...p, [item.key]: !p[item.key] }))}
                      className={"relative inline-flex w-9 h-5 rounded-full border-2 border-transparent flex-shrink-0 mt-0.5 transition-colors " +
                        (item.locked
                          ? "bg-stone-600 cursor-not-allowed"
                          : consent[item.key] ? "bg-white" : "bg-stone-600")}
                      aria-checked={consent[item.key]}
                      role="switch"
                    >
                      <span className={"inline-block w-4 h-4 bg-stone-900 rounded-full shadow transition-transform duration-200 " +
                        (consent[item.key] ? "translate-x-4" : "translate-x-0")} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col gap-2 mt-4">
              <button
                onClick={acceptAll}
                className="w-full py-2.5 bg-white text-stone-900 rounded-xl text-xs font-semibold hover:bg-stone-100 transition-colors"
              >
                Godta alle
              </button>
              <div className="flex gap-2">
                {expanded ? (
                  <button
                    onClick={saveCustom}
                    className="flex-1 py-2.5 bg-stone-700 text-white rounded-xl text-xs font-medium hover:bg-stone-600 transition-colors"
                  >
                    Lagre valg
                  </button>
                ) : (
                  <button
                    onClick={() => setExpanded(true)}
                    className="flex-1 py-2.5 bg-stone-800 text-stone-300 rounded-xl text-xs font-medium hover:bg-stone-700 hover:text-white transition-colors"
                  >
                    Tilpass
                  </button>
                )}
                <button
                  onClick={acceptNecessary}
                  className="flex-1 py-2.5 bg-stone-800 text-stone-300 rounded-xl text-xs font-medium hover:bg-stone-700 hover:text-white transition-colors"
                >
                  Kun nødvendige
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

// Fjerner informasjonskapslene GA4 og Meta allerede har satt når samtykket trekkes
// tilbake. Ellers ville identifikatorene ligge igjen og knyttes til neste besøk.
function slettSporingscookies(c: ConsentState) {
  const prefikser = [...(c.analytics ? [] : ['_ga', '_gid']), ...(c.marketing ? [] : ['_fbp', '_fbc', '_gcl'])];
  if (prefikser.length === 0) return;
  const rot = '.' + window.location.hostname.replace(/^www\./, '');
  for (const del of document.cookie.split(';')) {
    const navn = del.split('=')[0].trim();
    if (!prefikser.some(p => navn.startsWith(p))) continue;
    document.cookie = `${navn}=; Max-Age=0; path=/`;
    document.cookie = `${navn}=; Max-Age=0; path=/; domain=${rot}`;
  }
}

// Knapp for å endre eller trekke tilbake samtykket — like enkelt som å gi det.
export function EndreSamtykkeKnapp() {
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new Event(APNE_SAMTYKKE))}
      className="inline-block px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-xl hover:bg-stone-800 transition-colors"
    >
      Endre cookie-valg
    </button>
  );
}
